import React, { useCallback, useEffect, useRef, useState } from 'react';
import { coverageApi, reportApi, researchApi } from '../services/api';
import CompanyPicker from './CompanyPicker';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import { planLabel } from '../utils/planLabels';

const activeCoverageStatuses = new Set(['pending', 'building']);
const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const formatReportDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day} ${time}`;
};
const latestReportUpdate = (updates) => {
  const failure = updates.find((update) => update?.error);
  if (failure) return failure;
  if (updates.some((update) => !update)) return undefined;
  const published = updates.filter((update) => update.updatedAt);
  if (published.some((update) => Number.isNaN(new Date(update.updatedAt).getTime()))) {
    return { error: 'Invalid report update timestamp' };
  }
  return published.reduce((latest, update) => (
    !latest.updatedAt || new Date(update.updatedAt) > new Date(latest.updatedAt) ? update : latest
  ), { updatedAt: null });
};
const statusClass = (status) => ['pending', 'building'].includes(status) ? 'running' : status === 'delivered' ? 'succeeded' : ['failed', 'refused', 'cancelled'].includes(status) ? 'failed' : '';

const ResearchWorkspace = ({ user, onLogout, onOpenCompany, onOpenAdmin, onOpenHubs, onOpenPlans, onOpenProfile }) => {
  const [plan, setPlan] = useState({ id: user?.plan || 'client', label: user?.plan || 'Client', known: true });
  const [clientAccount, setClientAccount] = useState(Boolean(user?.is_client));
  const [companies, setCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const [complimentary, setComplimentary] = useState([]);
  const [freeCompany, setFreeCompany] = useState(null);
  const [reportUpdates, setReportUpdates] = useState({});
  const [included, setIncluded] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [firstlook, setFirstlook] = useState(null);
  const [requests, setRequests] = useState(null);
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [coverageTicker, setCoverageTicker] = useState(() => new URLSearchParams(window.location.search).get('ticker')?.trim().toUpperCase() || '');
  const [coverageNote, setCoverageNote] = useState('');
  const [coverageBusy, setCoverageBusy] = useState(false);
  const [coverageError, setCoverageError] = useState('');
  const [coverageNotice, setCoverageNotice] = useState('');
  const [coveredTicker, setCoveredTicker] = useState('');
  const [selectedTicker, setSelectedTicker] = useState('');
  const [companyLimit, setCompanyLimit] = useState(null);
  const [fullReportLimit, setFullReportLimit] = useState(0);
  const [fullReportCount, setFullReportCount] = useState(0);
  const [busyReport, setBusyReport] = useState('');
  const [firstlookTicker, setFirstlookTicker] = useState('');
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [allocationsError, setAllocationsError] = useState('');
  const [reconcileError, setReconcileError] = useState('');
  const [reconcileRetry, setReconcileRetry] = useState(0);
  const [addingCompany, setAddingCompany] = useState(false);
  const [error, setError] = useState('');
  const [fullReportConfirmation, setFullReportConfirmation] = useState(null);
  const dialogRef = useRef(null);
  const dialogTrigger = useRef(null);
  const dialogOpen = Boolean(fullReportConfirmation);
  const requestedCoverage = window.location.hash === '#request-coverage';

  useEffect(() => {
    if (requestedCoverage && !companiesLoading) document.getElementById('request-coverage')?.scrollIntoView?.({ block: 'start' });
  }, [requestedCoverage, companiesLoading]);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const previous = dialogTrigger.current;
    dialogRef.current?.querySelector('button')?.focus();
    return () => previous?.focus();
  }, [dialogOpen]);

  useEffect(() => {
    const loadCompanies = async () => {
      setCompaniesLoading(true);
      setAllocationsError('');
      try {
        const result = await researchApi.companies();
        setPlan(result.plan || { id: user?.plan || 'client', label: user?.plan || 'Client', known: true });
        setClientAccount(Boolean(result.is_client || user?.is_client));
        setCompanies(result.companies || []);
        setAvailableCompanies(result.available_companies || []);
        setComplimentary((result.complimentary || []).map((report) => ({
          ...report,
          ticker: report.ticker.toUpperCase()
        })));
        setIncluded(result.included || []);
        setFreeCompany(result.free_company || null);
        setUnlocks(result.unlocks || []);
        setFirstlook(result.firstlook || null);
        setRequests(result.requests || null);
        setCompanyLimit(result.limit);
        setFullReportLimit(result.full_report_limit);
        setFullReportCount(result.full_report_count ?? (result.companies || []).filter((company) => company.full_report && !company.upgrade_carryover).length);
        const last = sessionStorage.getItem('ci:lastTicker');
        const remembered = [...(result.companies || []), ...(result.complimentary || []), ...(result.free_company ? [result.free_company] : [])].find((company) => company.ticker === last);
        setSelectedTicker(remembered?.ticker || result.companies?.[0]?.ticker || result.complimentary?.[0]?.ticker || result.free_company?.ticker || '');
      } catch (err) {
        setAllocationsError(err.message);
        setError(err.message);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, [user?.plan, user?.is_client]);

  useEffect(() => {
    let active = true;
    const reconcile = async () => {
      setReconcileError('');
      const delays = [0, 500, 1500];
      for (let attempt = 0; attempt < delays.length; attempt += 1) {
        const delay = delays[attempt];
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
        if (!active) return;
        try {
          await researchApi.reconcile?.();
          return;
        } catch (err) {
          const retryable = !err.status || err.status === 409 || err.status === 429 || err.status >= 500;
          if (!retryable || attempt === delays.length - 1) {
            if (!active) return;
            setReconcileError(err.message || 'Profile updates could not be completed.');
            return;
          }
        }
      }
    };
    reconcile();
    return () => { active = false; };
  }, [user?.plan, reconcileRetry]);

  const isFreeTier = plan.id === 'tier_0';
  const isClient = clientAccount || plan.id === 'client';
  const isEmployee = plan.id === 'employee';
  const sharedClientTickers = new Set(complimentary.map((report) => report.ticker.toUpperCase()));
  const assignedTicker = freeCompany?.ticker?.toUpperCase() || (isFreeTier ? firstlook?.assigned_ticker?.toUpperCase() : null);
  const assignedCompany = assignedTicker && !companies.some((company) => company.ticker.toUpperCase() === assignedTicker)
    && !sharedClientTickers.has(assignedTicker)
    ? {
      ticker: assignedTicker,
      company_name: freeCompany?.company_name || availableCompanies.find((company) => company.ticker.toUpperCase() === assignedTicker)?.name || assignedTicker,
      assigned_firstlook: true
    } : null;
  const employeeCompanies = new Map(availableCompanies
    .filter((company) => company.live !== false)
    .map((company) => [company.ticker.toUpperCase(), { ...company, company_name: company.name || company.ticker }]));
  companies.forEach((company) => {
    const ticker = company.ticker.toUpperCase();
    employeeCompanies.set(ticker, { ...employeeCompanies.get(ticker), ...company, ticker });
  });
  if (assignedCompany) employeeCompanies.set(assignedTicker, assignedCompany);
  const trackedCompanies = isEmployee
    ? [...employeeCompanies.values()].sort((left, right) => left.ticker.localeCompare(right.ticker))
    : assignedCompany ? [...companies, assignedCompany]
      : isClient ? companies.filter((company) => !sharedClientTickers.has(company.ticker.toUpperCase()))
        : companies;
  const normalizedCompanyQuery = companyQuery.trim().toLowerCase();
  const visibleCompanies = trackedCompanies.filter((company) =>
    `${company.ticker} ${company.company_name || ''} ${company.subsection || ''}`.toLowerCase().includes(normalizedCompanyQuery)
  );
  const reportTickers = companiesLoading ? ''
    : [...new Set([...trackedCompanies, ...complimentary].map((company) => company.ticker.toUpperCase()))].sort().join(',');
  useEffect(() => {
    let active = true;
    setReportUpdates({});
    if (reportTickers && isEmployee) {
      const loadMetadata = async () => {
        const tickers = reportTickers.split(',');
        for (let offset = 0; active && offset < tickers.length; offset += 2000) {
          const batch = tickers.slice(offset, offset + 2000);
          const updates = {};
          try {
            const result = await reportApi.companyMetadata(batch);
            for (const ticker of batch) {
              for (const product of ['firstlook', 'deepdive']) {
                const metadata = result.companies?.[ticker]?.[product];
                if (metadata === undefined || (metadata !== null && typeof metadata.created_at !== 'string')) {
                  throw new Error('Report metadata response is incomplete.');
                }
                updates[`${ticker}:${product}`] = { updatedAt: metadata?.created_at || null };
              }
            }
          } catch (err) {
            for (const ticker of batch) {
              for (const product of ['firstlook', 'deepdive']) {
                updates[`${ticker}:${product}`] = { error: err.message };
              }
            }
          }
          if (active) setReportUpdates((current) => ({ ...current, ...updates }));
        }
      };
      loadMetadata();
    } else if (reportTickers) {
      const loadUpdate = async (ticker, product) => {
        try {
          const result = await reportApi.versions(ticker, product);
          if (active) setReportUpdates((current) => ({
            ...current, [`${ticker}:${product}`]: { updatedAt: result.versions[0]?.created_at || null }
          }));
        } catch (err) {
          if (active) setReportUpdates((current) => ({
            ...current, [`${ticker}:${product}`]: { error: err.message }
          }));
        }
      };
      const updates = reportTickers.split(',').flatMap((ticker) => [
        [ticker, 'firstlook'], [ticker, 'deepdive']
      ]);
      let nextUpdate = 0;
      const loadUpdates = async () => {
        while (active && nextUpdate < updates.length) {
          const [ticker, product] = updates[nextUpdate++];
          await loadUpdate(ticker, product);
        }
      };
      // Bound metadata requests for non-employee tracked and shared reports.
      for (let index = 0; index < Math.min(6, updates.length); index += 1) loadUpdates();
    }
    return () => { active = false; };
  }, [reportTickers, isEmployee, user?.plan]);

  const loadCoverageRequests = useCallback(async () => {
    const result = await coverageApi.mine();
    setCoverageRequests(result.items || []);
  }, []);

  useEffect(() => {
    if (!requests || !(requests.per_period > 0)) return undefined;
    loadCoverageRequests().catch((err) => setCoverageError(err.message));
    return undefined;
  }, [requests, loadCoverageRequests]);

  useEffect(() => {
    if (!coverageRequests.some((item) => activeCoverageStatuses.has(item.status))) return undefined;
    const timer = setInterval(() => {
      loadCoverageRequests().catch((err) => setCoverageError(err.message));
    }, 60000);
    return () => clearInterval(timer);
  }, [coverageRequests, loadCoverageRequests]);

  const addFirstLook = async (event) => {
    event.preventDefault();
    if (isFreeTier) {
      if (onOpenPlans) onOpenPlans();
      else window.location.assign('/plans');
      return;
    }
    if (isClient && complimentary.some((report) => report.ticker.toUpperCase() === firstlookTicker.toUpperCase())) {
      setError('This company is already available in Shared Client First Looks.');
      return;
    }
    const selected = firstlookChoices.find((company) => company.ticker === firstlookTicker);
    if (!selected) {
      setError('Select a live company from the roster before choosing a First Look.');
      return;
    }
    setAddingCompany(true);
    setError('');
    try {
      const picked = await researchApi.addFirstLookPick(selected.ticker, selected.name || selected.ticker);
      setCompanies((current) => {
        const company = picked.company || { ticker: picked.ticker, company_name: picked.company_name || selected.name };
        return current.some((item) => item.ticker === picked.ticker)
          ? current.map((item) => item.ticker === picked.ticker ? { ...item, ...company } : item)
          : [...current, company];
      });
      setFirstlook((current) => {
        if (picked.company?.upgrade_carryover) return current;
        if (fullReportLimit === null) {
          return {
            ...current,
            included_tickers: [...new Set([...(current?.included_tickers || []), picked.ticker])]
          };
        }
        const picks = [...new Set([...(current?.picks || []), picked.ticker])];
        const alreadyPicked = current?.picks?.includes(picked.ticker);
        return {
          ...current,
          picks,
          used: picks.length,
          remaining: current?.remaining == null || alreadyPicked
            ? current?.remaining
            : Math.max(current.remaining - 1, 0)
        };
      });
      setFirstlookTicker('');
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingCompany(false);
    }
  };

  const submitCoverageRequest = async (event) => {
    event.preventDefault();
    const normalized = coverageTicker.trim().toUpperCase();
    if (!normalized) return;
    setCoverageBusy(true);
    setCoverageError('');
    setCoverageNotice('');
    setCoveredTicker('');
    try {
      const item = await coverageApi.request(normalized, coverageNote.trim());
      setCoverageRequests((current) => [item, ...current.filter((request) => request.request_id !== item.request_id)]);
      if (!item.joined) {
        setRequests((current) => current ? { ...current, used: (current.used || 0) + 1, remaining: Math.max((current.remaining ?? 1) - 1, 0) } : current);
      }
      setCoverageTicker('');
      setCoverageNote('');
      setCoverageNotice(item.joined ? `Joined the existing ${item.ticker} research request.` : `${item.ticker} research request received.`);
    } catch (err) {
      if (err.status === 409 && err.body?.covered) {
        setCoveredTicker(normalized);
        setCoverageError(`${normalized} is already covered.`);
      } else if (err.status === 409 && (err.body?.detail || err.message).includes('No coverage requests remain')) {
        setCoverageError('No coverage requests remain this month.');
        setRequests((current) => current ? { ...current, remaining: 0 } : current);
      } else if (err.status === 403) {
        setCoverageError('Coverage requests are not available for this plan.');
      } else {
        setCoverageError(err.message);
      }
    } finally {
      setCoverageBusy(false);
    }
  };

  const selectedReports = fullReportCount;
  const showFullReportChoice = fullReportLimit != null && fullReportLimit > 0;
  const liveCompanies = availableCompanies.filter((company) => company.live !== false)
    .sort((left, right) => left.ticker.localeCompare(right.ticker));
  const trackedTickers = new Set(trackedCompanies.map((company) => company.ticker.toUpperCase()));
  const inheritedFirstLookTickers = new Set((firstlook?.included_tickers || []).map((ticker) => ticker.toUpperCase()));
  const usedTickers = new Set((firstlook?.picks || []).map((ticker) => ticker.toUpperCase()));
  const exemptTickers = new Set((firstlook?.exempt_tickers || []).map((ticker) => ticker.toUpperCase()));
  const untrackedUsedCount = [...usedTickers].filter((ticker) => !trackedTickers.has(ticker)).length;
  const firstlookChoices = liveCompanies.filter((company) => {
    const ticker = company.ticker.toUpperCase();
    if (ticker === assignedTicker || inheritedFirstLookTickers.has(ticker)) return false;
    if (isClient && sharedClientTickers.has(ticker)) return false;
    const used = usedTickers.has(ticker);
    const exempt = exemptTickers.has(ticker);
    return !(used && trackedTickers.has(company.ticker.toUpperCase()))
      && (isFreeTier || firstlook?.remaining == null || firstlook.remaining > 0 || used || exempt);
  });
  const personalCompanyCount = companies.filter((company) => !company.upgrade_carryover && company.ticker.toUpperCase() !== assignedTicker
    && !sharedClientTickers.has(company.ticker.toUpperCase())
    && !inheritedFirstLookTickers.has(company.ticker.toUpperCase())).length;
  const limitLabel = companyLimit == null ? 'unlimited' : companyLimit;

  const addDeepDive = async (company) => {
    setBusyReport(company.ticker);
    setError('');
    try {
      const result = await researchApi.setFullReport(company.ticker, true);
      setCompanies((current) => current.map((item) => item.ticker === company.ticker ? {
        ...item, full_report: result.full_report,
        upgrade_carryover: item.full_report ? item.upgrade_carryover : false
      } : item));
      if (result.full_report && !company.full_report) setFullReportCount((current) => current + 1);
      if (result.full_report) setFirstlook((current) => {
        if (!current) return current;
        const picks = (current.picks || []).filter((ticker) => ticker.toUpperCase() !== company.ticker.toUpperCase());
        return {
          ...current, picks, used: picks.length,
          remaining: current.per_period == null ? null : Math.max(current.per_period - picks.length, 0),
          included_tickers: [...new Set([...(current.included_tickers || []), company.ticker])]
        };
      });
      setFullReportConfirmation(null);
      if (result.full_report) {
        sessionStorage.setItem('ci:lastTicker', company.ticker);
        setSelectedTicker(company.ticker);
        onOpenCompany(company.ticker);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyReport('');
    }
  };

  const deepDiveState = (company) => {
    if (company.ticker.toUpperCase() === assignedTicker) return 'owned';
    if (isClient
      && complimentary.some((report) => report.ticker.toUpperCase() === company.ticker.toUpperCase())) return 'owned';
    if (fullReportLimit === null || (fullReportLimit > 0 && company.full_report) ||
      included.includes(company.ticker) || unlocks.some((unlock) => unlock.ticker === company.ticker)) return 'owned';
    return selectedReports < fullReportLimit ? 'available' : 'maxed';
  };

  const requestDeepDive = async (company, event) => {
    dialogTrigger.current = event.currentTarget;
    setError('');
    if (deepDiveState(company) === 'available') {
      setFullReportConfirmation({ type: 'company', company });
      return;
    }
    setFullReportConfirmation({ type: 'upgrade', company, loading: true });
    try {
      const catalog = await reportApi.plans();
      const current = catalog.plans.find((item) => item.id === plan.id);
      const nextPlan = current && catalog.plans
        .filter((item) => item.id !== 'employee' && item.rank > current.rank &&
          Object.prototype.hasOwnProperty.call(item.limits || {}, 'full_reports') &&
          (item.limits.full_reports === null || item.limits.full_reports > fullReportLimit))
        .sort((left, right) => left.rank - right.rank)[0];
      setFullReportConfirmation((dialog) => dialog?.type === 'upgrade' && dialog.company.ticker === company.ticker
        ? { ...dialog, loading: false, nextPlan } : dialog);
    } catch (err) {
      setFullReportConfirmation((dialog) => dialog?.type === 'upgrade' && dialog.company.ticker === company.ticker
        ? { ...dialog, loading: false, error: err.message } : dialog);
    }
  };

  const openCompany = (event, companyTicker) => {
    // Let modified clicks open the report in a new tab.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    sessionStorage.setItem('ci:lastTicker', companyTicker);
    setSelectedTicker(companyTicker);
    onOpenCompany(companyTicker);
  };
  const openFirstLook = (event, companyTicker) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    sessionStorage.setItem('ci:lastTicker', companyTicker);
    setSelectedTicker(companyTicker);
    onOpenCompany(companyTicker, 'firstlook');
  };

  const reportUpdate = (ticker, product) => {
    const update = product === 'latest'
      ? latestReportUpdate(['firstlook', 'deepdive'].map((item) => reportUpdates[`${ticker.toUpperCase()}:${item}`]))
      : reportUpdates[`${ticker.toUpperCase()}:${product}`];
    if (!update) return <small className="company-report-update">Loading update...</small>;
    if (update.error) return <small className="company-report-update" role="alert">Update unavailable: {update.error}</small>;
    return <small className={`company-report-update${update.updatedAt ? ' has-date' : ''}`}>{update.updatedAt
      ? <>Updated <time dateTime={update.updatedAt}>{formatReportDateTime(update.updatedAt)}</time></>
      : 'Not published yet'}</small>;
  };

  const companyRow = (company, shared = false) => {
    const state = shared ? 'owned' : deepDiveState(company);
    return <div className="company-entry" key={company.ticker}>
      <div className="company-mobile-heading">{company.ticker} / {company.company_name || company.ticker}</div>
      <a className={`company-option ${selectedTicker === company.ticker ? 'selected' : ''}`} href={`/companies/${encodeURIComponent(company.ticker)}?product=firstlook`} onClick={(event) => openFirstLook(event, company.ticker)} aria-label={`Open ${company.company_name || company.ticker} First Look`}>
        <span className="company-ticker">{company.ticker}</span><span className="company-mobile-action-label">First Look</span><span className="company-name"><span className="company-label">{company.company_name || company.ticker}</span>{company.upgrade_carryover && <small>Retained after upgrade until month-end</small>}{reportUpdate(company.ticker, 'firstlook')}</span><span className="company-arrow" aria-hidden="true">↗</span>
      </a>
      {state === 'owned' ? <a className="deep-dive-action owned" href={`/companies/${encodeURIComponent(company.ticker)}`} onClick={(event) => openCompany(event, company.ticker)} aria-label={`Open ${company.ticker} Deep Dive`}>Deep Dive{!shared && <small>{isFreeTier && company.ticker.toUpperCase() === assignedTicker ? 'Included with your plan' : 'Owned'}</small>}{reportUpdate(company.ticker, 'deepdive')}</a>
        : <button className={`deep-dive-action ${state}`} type="button" disabled={Boolean(busyReport)} onClick={(event) => requestDeepDive(company, event)} aria-label={`${state === 'available' ? 'Add' : 'Upgrade for'} ${company.ticker} Deep Dive`}>Deep Dive<small>{state === 'available' ? 'Add to this month' : 'Upgrade plan'}</small>{reportUpdate(company.ticker, 'deepdive')}</button>}
      <div className="company-mobile-updates">
        {reportUpdate(company.ticker, 'latest')}
      </div>
    </div>;
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand />
        <HeaderNav user={user} planLabel={planLabel(plan.id, plan.label)} onLogout={onLogout} onOpenHubs={onOpenHubs} onOpenPlans={onOpenPlans} onOpenAdmin={onOpenAdmin} onOpenProfile={onOpenProfile} />
      </header>
      <main className="workspace">
        {reconcileError && <div className="error-box workspace-error" role="alert">
          Profile updates could not be completed: {reconcileError}{' '}
          <button className="secondary-button" type="button" onClick={() => setReconcileRetry((value) => value + 1)}>Retry profile update</button>
        </div>}
        <section className="workspace-intro">
          <div>
            <p className="eyebrow">Market intelligence / beta</p>
            <h1>Find the signal.<br /><em>Eliminate the noise.</em></h1>
          </div>
          <p className="intro-copy">Company filings, earnings calls, and investor materials—put in context with financial results, analyst estimates, news, insider activity, and market data.</p>
        </section>

        {error && !dialogOpen && <div className="error-box workspace-error" role="alert">{error}</div>}

        <section className="company-layout">
          <article className="panel company-directory">
            <div className="panel-heading"><div><p className="eyebrow">Company report access</p><h2>Tracked companies</h2></div><div className="panel-heading-meta"><span className="quality-label">{isFreeTier ? `${trackedCompanies.length} ${trackedCompanies.length === 1 ? 'company' : 'companies'}` : `${isEmployee ? trackedCompanies.length : personalCompanyCount} / ${limitLabel}`}</span><span className="full-report-count">{isEmployee ? 'Full live roster and tracked access' : 'Currently tracked'}</span>{showFullReportChoice && <span className="full-report-count">{selectedReports} / {fullReportLimit} Deep Dives selected</span>}</div></div>
            <p className="muted">Select a company for First Look.</p>
            {!companiesLoading && trackedCompanies.length > 0 && <div className="company-list-filter">
              <label htmlFor="tracked-company-search">Search companies</label>
              <input id="tracked-company-search" type="search" value={companyQuery} onChange={(event) => setCompanyQuery(event.target.value)} placeholder="Search by company, ticker or subsection" aria-controls="tracked-company-list" />
              <span className="muted" role="status">{visibleCompanies.length} of {trackedCompanies.length} companies shown</span>
            </div>}
            {companiesLoading ? <div className="company-loading" role="status" aria-live="polite">
              <span className="company-loading-spinner" aria-hidden="true" />
              <p>Loading your companies...</p>
            </div> : trackedCompanies.length ? <div id="tracked-company-list" className={`company-list ${isEmployee ? 'employee-company-list' : ''}`}>{visibleCompanies.length ? visibleCompanies.map((company) => companyRow(company)) : <div className="empty-state">No companies match your search.</div>}</div> : <div className="empty-state">{isEmployee ? 'No roster companies or tracked access are available.' : 'You are not currently tracking any companies.'}</div>}
          </article>
          <article className="panel plan-panel">
            <p className="eyebrow">Your access</p>
            <div className="plan-number">{planLabel(plan.id, plan.label)}</div>
            <h2>{planLabel(plan.id, plan.label)} access</h2>
            {companiesLoading ? <p className="muted">Loading report allocations...</p> : allocationsError ? <p className="muted" role="alert">Report allocations could not be loaded: {allocationsError}</p> : <>
              <p className="muted">{isFreeTier ? (assignedTicker ? 'First Look: your admin-assigned company is included.' : 'First Look: your company is assigned by an admin.') : firstlook ? (firstlook.all ? 'First Look: all reports included.' : firstlook.per_period == null ? `First Look: ${firstlook.used} used this month; unlimited allocation.` : `First Look: ${firstlook.used} / ${firstlook.per_period} used this month; ${firstlook.remaining} remaining (${firstlook.period}).`) : 'First Look allocation unavailable.'}</p>
              <div className="tier-rule"><span />{isFreeTier ? (assignedTicker ? 'Deep Dive: included for your admin-assigned company.' : 'Deep Dive: included once your company is assigned.') : fullReportLimit === null ? 'Deep Dive: unlimited selections.' : fullReportLimit === undefined ? 'Deep Dive allocation unavailable.' : fullReportLimit > 0 ? `Deep Dive: ${selectedReports} / ${fullReportLimit} selected; ${Math.max(fullReportLimit - selectedReports, 0)} remaining.` : 'Deep Dive: no personal selections included.'}</div>
              {isClient && <p className="muted">Shared Client reports include First Look and Deep Dive without using your personal allocations.</p>}
              {freeCompany && <p className="muted">{freeCompany.ticker} includes First Look and Deep Dive on every tier without using your personal allocations.</p>}
              <p className="muted">Every active Deep Dive includes First Look for free. Upgrading a First Look returns that company&apos;s selection to your monthly allowance.</p>
              {!isFreeTier && included.length > 0 && <p className="muted">Plan-included reports do not use your personal allocations.</p>}
            </>}
          </article>
        </section>

        {complimentary.length > 0 && <section className="panel company-directory">
          <div className="panel-heading"><div><p className="eyebrow">Client access</p><h2>Shared Client Companies</h2></div></div>
          <p className="muted">First Look and Deep Dive included for Clients.</p>
          <div className="company-list">{complimentary.map((report) => companyRow(report, true))}</div>
        </section>}

        {!isFreeTier && included.length > 0 && <section className="panel company-directory">
          <div className="panel-heading"><div><p className="eyebrow">Included</p><h2>Included with your plan</h2></div></div>
          <div className="company-list">{included.map((includedTicker) => <a className="company-option" key={includedTicker} href={`/companies/${encodeURIComponent(includedTicker)}`} onClick={(event) => openCompany(event, includedTicker)}><span className="company-ticker">{includedTicker}</span><span className="company-name">Included with your plan</span><span className="company-arrow" aria-hidden="true">↗</span></a>)}</div>
        </section>}

        {!isEmployee && firstlook && <section className="panel company-directory firstlook-panel">
          <div className="panel-heading">
            <div><p className="eyebrow">{isFreeTier ? 'More company reports' : 'Monthly allowance'}</p><h2>First Look reports</h2></div>
            <span className="quality-label">{isFreeTier ? 'Upgrade to choose' : firstlook.all ? 'All included' : firstlook.per_period === 0 ? 'Admin assigned' : firstlook.per_period == null ? `${firstlook.used} used · unlimited` : `${firstlook.used} / ${firstlook.per_period} used`}</span>
          </div>
          {firstlook.all ? <p className="muted">All First Look reports are included with your Employee plan.</p> : null}
          {!isFreeTier && firstlook.assigned_ticker && <div className="company-list"><a className="company-option" href={`/companies/${encodeURIComponent(firstlook.assigned_ticker)}?product=firstlook`} onClick={(event) => openFirstLook(event, firstlook.assigned_ticker)}><span className="company-ticker">{firstlook.assigned_ticker}</span><span className="company-name">Admin-assigned First Look for this month</span><span className="company-arrow" aria-hidden="true">↗</span></a></div>}
          {(isFreeTier || (!firstlook.all && firstlook.per_period !== 0 && (firstlook.remaining == null || firstlook.remaining > 0 || firstlookChoices.length > 0))) && !companiesLoading && <form className="add-company-form roster-pick-form" onSubmit={addFirstLook}>
            <CompanyPicker companies={firstlookChoices} value={firstlookTicker} onChange={setFirstlookTicker} label="First Look company" disabled={addingCompany} />
            <button className="primary-button" type="submit" disabled={!isFreeTier && (addingCompany || !firstlookChoices.some((company) => company.ticker === firstlookTicker))}>{addingCompany ? 'Adding...' : 'Choose First Look'} <span aria-hidden="true">+</span></button>
          </form>}
          {isFreeTier ? <p className="muted">Upgrade your plan to choose more First Look reports. Your admin-assigned company remains included.</p> : !firstlook.all && firstlook.per_period !== 0 && <p className="muted">Your allowance resets monthly ({firstlook.period}).</p>}
          {!isFreeTier && !firstlook.all && untrackedUsedCount > 0 && <p className="muted">{untrackedUsedCount} {untrackedUsedCount === 1 ? 'selection used this month is' : 'selections used this month are'} no longer tracked. Removing or expiring a company does not refund monthly usage. Reselect an available company already used this month to track it again without using another selection.</p>}
        </section>}

        {(requests?.per_period > 0 || requestedCoverage) && <section id="request-coverage" className="panel company-directory coverage-panel">
          <div className="panel-heading">
            <div><p className="eyebrow">On-demand research</p><h2>Request New Coverage</h2></div>
            {requests && <span className="quality-label">{requests.remaining} of {requests.per_period} left this month</span>}
          </div>
          <p className="muted">Ask Rebellionaire.AI to build a new company report{requests?.sla_hours ? `; target SLA is ${requests.sla_hours} hours.` : '.'}</p>
          {!companiesLoading && !(requests?.per_period > 0) && !allocationsError && <p>Your plan does not include new coverage requests. <a href="/plans">View plans</a> to upgrade.</p>}
          {coverageError && <div className="error-box workspace-error" role="alert">{coverageError} {coveredTicker && <a href={`/companies/${encodeURIComponent(coveredTicker)}`} onClick={(event) => openCompany(event, coveredTicker)}>Open {coveredTicker}</a>}</div>}
          {coverageNotice && <div className="success-box admin-message" role="status">{coverageNotice}</div>}
          <form className="add-company-form coverage-request-form" onSubmit={submitCoverageRequest}>
            <label><span>Ticker</span><input value={coverageTicker} onChange={(event) => setCoverageTicker(event.target.value)} placeholder="UPST" maxLength={10} pattern="[A-Za-z0-9.-]+" required /></label>
            <label><span>Note (optional)</span><input value={coverageNote} onChange={(event) => setCoverageNote(event.target.value)} placeholder="What should we focus on?" maxLength={500} /></label>
            <button className="primary-button" type="submit" disabled={companiesLoading || coverageBusy || !(requests?.remaining > 0)}>{coverageBusy ? 'Requesting...' : 'Request research'}</button>
          </form>
          <div className="panel-heading requests-heading"><div><p className="eyebrow">Status</p><h2>My requests</h2></div></div>
          {coverageRequests.length ? <div className="company-list request-list">{coverageRequests.map((item) => (
            <div className="company-option request-entry" key={item.request_id}>
              <span className="company-ticker">{item.ticker}</span>
              <span className="company-name">
                {item.company_name || item.ticker} · requested {formatDateTime(item.created_at || item.requesters?.[0]?.requested_at)}
                {item.sla_due ? ` · SLA due ${formatDateTime(item.sla_due)}` : ''}
                {['refused', 'failed'].includes(item.status) && item.error ? ` · ${item.error}` : ''}
                {item.status === 'delivered' && <a className="coverage-open-link" href={`/companies/${encodeURIComponent(item.ticker)}`} onClick={(event) => openCompany(event, item.ticker)}>Open company page</a>}
              </span>
              <span className={`pill ${statusClass(item.status)}`}>{item.status}</span>
            </div>
          ))}</div> : <div className="empty-state">No research requests yet.</div>}
        </section>}

      </main>
      {fullReportConfirmation && <div className="modal-backdrop" role="presentation"><section ref={dialogRef} className="confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="full-report-confirmation-title" onKeyDown={(event) => {
        if (event.key === 'Escape' && !busyReport) setFullReportConfirmation(null);
        if (event.key === 'Tab') {
          const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), a[href]')];
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
          if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
        }
      }}>
        <p className="eyebrow">Deep Dive access</p>
        <h2 id="full-report-confirmation-title">{fullReportConfirmation.type === 'company' ? `Add ${fullReportConfirmation.company.ticker} Deep Dive?` : 'Get more Deep Dives'}</h2>
        {fullReportConfirmation.type === 'company' ? <>
          <p>This uses one of your plan's Deep Dive selections. You have {Math.max(fullReportLimit - selectedReports, 0)} of {fullReportLimit} remaining. This choice cannot be undone here.</p>
          <p>First Look is included for free. Any First Look selection used for this company this month is returned to your allowance.</p>
          {error && <p className="error-box" role="alert">{error}</p>}
          <div className="modal-actions"><button className="secondary-button" type="button" disabled={Boolean(busyReport)} onClick={() => setFullReportConfirmation(null)}>Cancel</button><button className="primary-button" type="button" disabled={Boolean(busyReport)} onClick={() => addDeepDive(fullReportConfirmation.company)}>{busyReport ? 'Adding...' : 'Confirm and open Deep Dive'}</button></div>
        </> : <>
          <p>{fullReportLimit === 0 ? 'Your current plan does not include Deep Dive selections.' : `You have used all ${fullReportLimit} Deep Dive selections available on your current plan.`}</p>
          <p>Every active Deep Dive includes that company&apos;s First Look for free. Choosing its Deep Dive returns any First Look selection used for that company this month to your allowance.</p>
          {fullReportConfirmation.loading ? <p role="status">Finding your next tier...</p> : fullReportConfirmation.nextPlan ? <p>Upgrade to {fullReportConfirmation.nextPlan.label || fullReportConfirmation.nextPlan.id} for {fullReportConfirmation.nextPlan.limits.full_reports == null ? 'unlimited' : fullReportConfirmation.nextPlan.limits.full_reports} Deep Dives.</p> : <p>Compare plans for more access or a custom plan.</p>}
          {fullReportConfirmation.error && <p className="error-box" role="alert">Could not load upgrade options: {fullReportConfirmation.error}</p>}
          <div className="modal-actions"><button className="secondary-button" type="button" onClick={() => setFullReportConfirmation(null)}>Not now</button><a className="primary-button" href="/plans" onClick={(event) => {
            if (onOpenPlans && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); setFullReportConfirmation(null); onOpenPlans(); }
          }}>{fullReportConfirmation.nextPlan ? `Explore ${fullReportConfirmation.nextPlan.label || fullReportConfirmation.nextPlan.id}` : 'Compare plans'}</a></div>
        </>}
      </section></div>}
    </div>
  );
};

export default ResearchWorkspace;
